'use client';

import { useState } from 'react';
import {
	XMarkIcon,
	MagnifyingGlassIcon,
	UserIcon,
	CreditCardIcon,
	BanknotesIcon,
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { apiService } from '../services/api';
import Button from '../components/button';
import { toast } from 'sonner';

const CreditCheckModal = ({ closeModal }) => {
	const [loading, setLoading] = useState(false);
	const [creditResult, setCreditResult] = useState(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm();

	const onSubmit = async (data) => {
		setLoading(true);
		try {
			const response = await apiService.performCreditCheck({
				bvn: data.bvn,
			});

			if (response.data.status === 200) {
				setCreditResult(response.data.data);
				toast.success('Credit check completed successfully!');
			} else {
				throw new Error(response.data.message || 'Credit check failed');
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Credit check failed';
			toast.error(message);
			setCreditResult(null);
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		reset();
		setCreditResult(null);
	};

	const getRatingColor = (rating) => {
		switch (rating) {
			case 'EXCELLENT':
				return 'text-green-600 bg-green-100';
			case 'GOOD':
				return 'text-blue-600 bg-blue-100';
			case 'FAIR':
				return 'text-yellow-600 bg-yellow-100';
			case 'POOR':
				return 'text-red-600 bg-red-100';
			default:
				return 'text-gray-600 bg-gray-100';
		}
	};

	const getRiskColor = (level) => {
		switch (level) {
			case 'LOW':
				return 'text-green-600 bg-green-100';
			case 'MEDIUM':
				return 'text-yellow-600 bg-yellow-100';
			case 'HIGH':
				return 'text-red-600 bg-red-100';
			default:
				return 'text-gray-600 bg-gray-100';
		}
	};

	return (
		<div className='fixed inset-0 modal-backdrop z-[1000] flex items-center justify-center bg-black bg-opacity-50'>
			<div
				className='fixed inset-0'
				onClick={closeModal}></div>
			<div className='relative bg-white  rounded-lg shadow-xl mx-4 max-h-[90vh] overflow-y-auto'>
				<div className='flex items-center justify-between p-6 border-b border-gray-200'>
					<h2 className='text-xl font-semibold text-gray-900'>
						Credit Check
					</h2>
					<button
						onClick={closeModal}
						className='text-gray-400 hover:text-gray-500'>
						<XMarkIcon className='h-6 w-6' />
					</button>
				</div>
				<div className='relative bg-white rounded-xl shadow-xl max-w-lg w-full'>
					{/* Content */}
					<div className='p-6 max-h-[calc(90vh-140px)] overflow-y-auto'>
						{!creditResult ? (
							/* Search Form */
							<div className='max-w-md mx-auto'>
								<div className='text-center mb-6'>
									<div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
										<MagnifyingGlassIcon className='h-8 w-8 text-blue-600' />
									</div>
									<h3 className='text-lg font-medium text-gray-900 mb-2'>
										Search Customer
									</h3>
									<p className='text-sm text-gray-600'>
										Enter the customer's BVN to perform a
										credit check
									</p>
								</div>

								<form
									onSubmit={handleSubmit(onSubmit)}
									className='space-y-4'>
									<div>
										<label
											htmlFor='bvn'
											className='block text-sm font-medium text-gray-700 mb-2'>
											BVN (Bank Verification Number)
										</label>
										<input
											{...register('bvn', {
												required: 'BVN is required',
												pattern: {
													value: /^\d{11}$/,
													message:
														'BVN must be exactly 11 digits',
												},
											})}
											type='text'
											className='input-field w-full'
											placeholder='Enter 11-digit BVN'
											maxLength={11}
										/>
										{errors.bvn && (
											<p className='mt-1 text-sm text-red-600'>
												{errors.bvn.message}
											</p>
										)}
									</div>

									<div className='flex space-x-3 pt-4'>
										<Button
											type='primary'
											label={
												loading
													? 'Searching...'
													: 'Search Customer'
											}
											iconLeft={
												!loading && (
													<MagnifyingGlassIcon className='h-4 w-4' />
												)
											}
											onClick={handleSubmit(onSubmit)}
											loading={loading}
											disabled={loading}
											className='flex-1'
										/>
										<Button
											type='secondary'
											variant='outlined'
											label='Reset'
											onClick={resetForm}
											disabled={loading}
										/>
									</div>
								</form>
							</div>
						) : (
							/* Credit Check Results */
							<div className='space-y-6'>
								{/* Customer Info */}
								<div className='bg-gray-50 rounded-lg p-6'>
									<div className='flex items-center mb-4'>
										<UserIcon className='h-5 w-5 text-gray-600 mr-2' />
										<h3 className='text-lg font-medium text-gray-900'>
											Customer Information
										</h3>
									</div>
									<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
										<div>
											<p className='text-sm text-gray-600'>
												Name
											</p>
											<p className='font-medium text-gray-900'>
												{creditResult.user.name}
											</p>
										</div>
										<div>
											<p className='text-sm text-gray-600'>
												BVN
											</p>
											<p className='font-medium text-gray-900'>
												{creditResult.user.bvn}
											</p>
										</div>
										<div>
											<p className='text-sm text-gray-600'>
												Email
											</p>
											<p className='font-medium text-gray-900'>
												{creditResult.user.email}
											</p>
										</div>
										<div>
											<p className='text-sm text-gray-600'>
												Phone
											</p>
											<p className='font-medium text-gray-900'>
												{creditResult.user.phone}
											</p>
										</div>
										<div className='md:col-span-2'>
											<p className='text-sm text-gray-600'>
												Address
											</p>
											<p className='font-medium text-gray-900'>
												{creditResult.user.address}
											</p>
										</div>
									</div>
								</div>

								{/* Credit Score & Risk Assessment */}
								<div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
									<div className='bg-white border border-gray-200 rounded-lg p-6 text-center'>
										<div className='w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3'>
											<span className='text-xl font-bold text-blue-600'>
												{creditResult.creditScore.score}
											</span>
										</div>
										<h4 className='font-medium text-gray-900 mb-1'>
											Credit Score
										</h4>
										<span
											className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getRatingColor(
												creditResult.creditScore.rating,
											)}`}>
											{creditResult.creditScore.rating}
										</span>
									</div>

									<div className='bg-white border border-gray-200 rounded-lg p-6 text-center'>
										<div className='w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3'>
											<CreditCardIcon className='h-6 w-6 text-gray-600' />
										</div>
										<h4 className='font-medium text-gray-900 mb-1'>
											Total Debts
										</h4>
										<p className='text-lg font-semibold text-gray-900'>
											{creditResult.user.totalDebts}
										</p>
									</div>

									<div className='bg-white border border-gray-200 rounded-lg p-6 text-center'>
										<div className='w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3'>
											<span className='text-sm font-bold text-gray-600'>
												RISK
											</span>
										</div>
										<h4 className='font-medium text-gray-900 mb-1'>
											Risk Level
										</h4>
										<span
											className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(
												creditResult.riskAssessment
													.riskLevel,
											)}`}>
											{
												creditResult.riskAssessment
													.riskLevel
											}
										</span>
									</div>
								</div>

								{/* Financial Summary */}
								<div className='bg-white border border-gray-200 rounded-lg p-6'>
									<div className='flex items-center mb-4'>
										<BanknotesIcon className='h-5 w-5 text-gray-600 mr-2' />
										<h3 className='text-lg font-medium text-gray-900'>
											Financial Summary
										</h3>
									</div>
									<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
										<div>
											<p className='text-sm text-gray-600'>
												Avg Monthly Inflow
											</p>
											<p className='font-medium text-gray-900'>
												₦
												{creditResult.financialSummary.avgMonthlyInflow.toLocaleString()}
											</p>
										</div>
										<div>
											<p className='text-sm text-gray-600'>
												Avg Monthly Outflow
											</p>
											<p className='font-medium text-gray-900'>
												₦
												{creditResult.financialSummary.avgMonthlyOutflow.toLocaleString()}
											</p>
										</div>
										<div>
											<p className='text-sm text-gray-600'>
												Net Cash Flow
											</p>
											<p className='font-medium text-gray-900'>
												₦
												{creditResult.financialSummary.netCashFlow.toLocaleString()}
											</p>
										</div>
										<div>
											<p className='text-sm text-gray-600'>
												Debt-to-Income Ratio
											</p>
											<p className='font-medium text-gray-900'>
												{
													creditResult
														.financialSummary
														.debtToIncomeRatio
												}
												%
											</p>
										</div>
									</div>
								</div>

								{/* Bank Accounts */}
								{creditResult.user.accounts &&
									creditResult.user.accounts.length > 0 && (
										<div className='bg-white border border-gray-200 rounded-lg p-6'>
											<h3 className='text-lg font-medium text-gray-900 mb-4'>
												Bank Accounts
											</h3>
											<div className='space-y-3'>
												{creditResult.user.accounts.map(
													(account, index) => (
														<div
															key={index}
															className='flex justify-between items-center p-3 bg-gray-50 rounded-lg'>
															<div>
																<p className='font-medium text-gray-900'>
																	{
																		account.bank
																	}
																</p>
																<p className='text-sm text-gray-600'>
																	{
																		account.accountNumber
																	}{' '}
																	•{' '}
																	{
																		account.accountType
																	}
																</p>
															</div>
															<p className='font-medium text-gray-900'>
																₦
																{account.balance.toLocaleString()}
															</p>
														</div>
													),
												)}
											</div>
										</div>
									)}

								{/* Risk Factors */}
								{creditResult.riskAssessment.factors &&
									creditResult.riskAssessment.factors.length >
										0 && (
										<div className='bg-white border border-gray-200 rounded-lg p-6'>
											<h3 className='text-lg font-medium text-gray-900 mb-4'>
												Risk Factors
											</h3>
											<ul className='space-y-2'>
												{creditResult.riskAssessment.factors.map(
													(factor, index) => (
														<li
															key={index}
															className='flex items-center text-sm text-gray-600'>
															<span className='w-2 h-2 bg-yellow-400 rounded-full mr-3'></span>
															{factor}
														</li>
													),
												)}
											</ul>
										</div>
									)}

								{/* Actions */}
								<div className='flex space-x-3 pt-4'>
									<Button
										type='secondary'
										variant='outlined'
										label='New Search'
										onClick={resetForm}
										className='flex-1'
									/>
									<Button
										type='primary'
										label='Close'
										onClick={closeModal}
										className='flex-1'
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default CreditCheckModal;
